import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import FormInputs from 'apps/web_forms/components/Form/FormInputs';
import {
  addGroupToList,
  addRepeatGroupFields,
  removeRootLevelField,
  removeGroupFromList, 
  removeRepeatGroupField, 
  removeSubformField, 
  updateRules, 
  removeSubform,
} from 'apps/web_forms/slices/formSlice';
import { COLORS, DEVICES_SIZES } from 'apps/web_forms/utils/constants';
import CloseIcon from 'apps/web_forms/assets/close.svg';
import CheckIcon from 'apps/web_forms/assets/check.svg';
import  FormIcon  from 'apps/web_forms/assets/form.svg';
import ProgressCircle from 'apps/web_forms/components/commons/ProgressCircle';
import useGroupProgress from 'apps/web_forms/hooks/useGroupProgress';
import {
  selectGroupListByIdentifier,
  selectHasGroupListIdentifier,
  selectImageDraftValues,
  selectImagesToSubmit,
  selectRootLevelFields,
} from 'apps/web_forms/hooks/useGlobalSelector';
import ComponentModal from 'apps/web_forms/components/commons/ComponentModal';
import MediaFormControls from 'apps/web_forms/components/MediaFormControls';
import { getIdWithoutSuffix } from 'apps/web_forms/utils/inputsUtils';
import { removeImageToSubmit, removeImageDraftValue } from 'apps/web_forms/slices/multimediaSlice';

const StyledGroupContainer = styled.div`
  background-color: ${COLORS.inputBackgroundColor};
  border: 1px solid ${COLORS.grey};
  padding: 1em;
  margin-bottom: 1em;
  & > .title-container {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    cursor: initial;
    &.clickable-title {
      cursor: pointer;
    }
    &:focus {
      outline: none;
    }
    h5 {
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 300;
      font-size: 0.95em;
      flex-grow: 1;
    }
    .group-contains-inputs {
      polygon {
        fill: ${COLORS.lightgrey};
      }
    }
    .collapse-expand-btn,
    .close-btn {
      width: 1.9em;
      height: 1.9em;
      background-color: transparent;
      border: none;
    }
    .close-btn {
      display: block;
      svg {
        width: 0.9em;
        height: 0.9em;
        fill: ${COLORS.grey};
      }
    }

    .completed-circle {
      position: relative;
      margin-right: 1em;
    }
    .completed-circle .check-icon {
      position: absolute;
      width: 16px;
      height: 16px;
      top: calc(50% - 8px);
      left: calc(50% - 8px);
      border-radius: 50%;
      transition: all 200ms;
    }
  }
 
  & > .repeat-group-inputs-container {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-end;
    display: ${(props) => (props.isOpenGroup ? 'flex' : 'none')};
    .elements-container {
      width: 100%;
    }
    .cancel-btn,
    .done-btn {
      border: none;
      font-size: 0.75em;
      font-weight: 600;
      border-radius: 5em;
      padding: 0.5em 2.3em;
    }
    .cancel-btn {
      background-color: transparent;
      color: ${COLORS.red};
      font-weight: 500;
      padding-right: 1.9em;
    }
    .done-btn {
      background-color: ${COLORS.green};
      color: white;
    }
  }
`;

const StyledFormElementsContainer = styled.div`
  background-color: white;
  padding: 1.9em;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  flex-grow: 1;
  overflow: auto;
  .elements-container {
    width: 100%;
    padding-right: 1em;
  }

  @media (min-width: ${DEVICES_SIZES.tablet}) {
    .elements-container {
      width: ${(props) => (props.isOpenGroup ? '59%' : '95%')};
      padding: 0;
      transition: 0.5s;
    }
  } ;
`;

const Group = ({
  identifier,
  groupElements,
  groupId,
  onDelete,
  formContext,
  onChangeValue,
}) => {
  const [isOpenGroup, setIsOpenGroup] = useState(true);
  const [isDone, setIsDone] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const containsInputs = groupElements.some(item => item.type !== 'group');
  
  const identifierWithoutSuffixes = getIdWithoutSuffix(identifier);
  const groupsForIdentifier = useSelector((state) =>
    selectGroupListByIdentifier(state, identifierWithoutSuffixes)
  );
  const hasGroupsForIdentifier = useSelector((state) =>
    selectHasGroupListIdentifier(state, identifierWithoutSuffixes)
  );
  const rootLevelFields = useSelector(selectRootLevelFields);
  const imagesToSubmit = useSelector(selectImagesToSubmit);
  const imageDraftValues = useSelector(selectImageDraftValues);

  const [width, setWidth] = useState(window.innerWidth);

  const { setValue, unregister } = formContext;
  const dispatch = useDispatch();

  const { completedPercentage, updateField } = useGroupProgress(
    identifier,
    setValue
  );

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
        window.removeEventListener('resize', handleWindowSizeChange);
    }
  }, []);

  function onChildChangeValue(childIdentifier, value) {
    updateField(childIdentifier, value);
  }

  useEffect(() => {
    function storeNewRepeatGroupFields(fields, repeatGroupFields = []) {
      const newFields = fields;

      newFields.forEach((field) => {
        if (field.children && field.maxOccurs) {
          // Subform
          storeNewRepeatGroupFields(field.children, repeatGroupFields);
        } else if (!field.children) {
          repeatGroupFields.push(field.identifier);
        }
      }, []);

      return repeatGroupFields;
    }
    /**
     * Checks if the group exists in groupList redux state or not. It should add it only if it doesn't exists.
     */
    const updateRepeatGroupFieldsSlice = storeNewRepeatGroupFields(groupElements);
    if (updateRepeatGroupFieldsSlice.length > 0) {
     dispatch(addRepeatGroupFields({ updateRepeatGroupFields: updateRepeatGroupFieldsSlice }));
    }

    if (
      !hasGroupsForIdentifier ||
      groupsForIdentifier.filter(g => g === `${identifier}|${groupId}`).length === 0
    ) {
      dispatch(addGroupToList({ identifier, suffix: groupId }));
    }

    return () => {
      deleteGroupFieldsCleanUp(groupElements);
      dispatch(removeGroupFromList({ identifier, suffix: groupId }));
    };
  }, []);

  const openGroup = (e) => {
    e.stopPropagation();
    if(containsInputs){
      setOpenModal(true);
    }
  };

  const onClickDone = () => {
    setIsDone(true);
  };

  function deleteGroupFieldsCleanUp(fieldsToRemove) {
    let arrayOfIdentifiers = [];
    function removeField(field) {
      if (field.children) {
        field.children.forEach((child) => removeField(child));
        return;
      }

      const childIdentifier = field.identifier;

      arrayOfIdentifiers.push(childIdentifier)

      onChangeValue(childIdentifier, undefined);
      unregister(childIdentifier);
    }
    fieldsToRemove.forEach((field) => { removeField(field) });
    dispatch(removeRepeatGroupField({ arrayOfIdentifiers }));
  }

  const removeGroup = (groupId) => {
    let groupsToCleanUp = [];

    function deleteGroupFields(field, isSubformField = false) {
      if (field.children) {
        groupsToCleanUp.push(field.identifier)
        if(field.hasOwnProperty("minOccurs") && field.hasOwnProperty("maxOccurs")){
          dispatch(removeSubform({identifier: field.identifier}))
          field.children.forEach((child) => deleteGroupFields(child, true));
          return;
        }
        field.children.forEach((child) => deleteGroupFields(child));
        return;
      }

      const childIdentifier = field.identifier;
      
      if(isSubformField){
        /**
         * There is a property in forms state in redux called subformField
         * and those fields should be remove there too if they are inside a group
         */
        dispatch(removeSubformField({ identifier: childIdentifier }));
      }
      if(imageDraftValues.hasOwnProperty(childIdentifier)){
         /**
         * If there are image draft values for a group and that group is removed,
         * the images from the redux state imageDraftValues should be removed too,
         * otherwise they will be submitted 
         */
        dispatch(removeImageDraftValue(childIdentifier))
      }
      if(imagesToSubmit.hasOwnProperty(childIdentifier)){
        /**
         * If there are images to submit values for a group and that group is removed,
         * the images from the redux state imagesToSubmit should be removed too,
         * otherwise they will be submitted 
         */
        dispatch(removeImageToSubmit(childIdentifier))
      }
      if (rootLevelFields.hasOwnProperty(childIdentifier)) {
        /**
         * If the field is a root level field, it should be removed from the rootLevelFields redux state
         */
        dispatch(removeRootLevelField({identifier: childIdentifier}));
      }
      dispatch(
        updateRules({
          identifier: childIdentifier,
          value: undefined,
        })
      );
    }
    if(groupId !== ''){
      /**
       * If entered here, means that this is the group clicked by the user to be removed
       */
      groupElements.forEach((element) => deleteGroupFields(element));
      dispatch(removeGroupFromList({ identifier, suffix: groupId }));
    }
    
    if(groupsToCleanUp.length > 0){
      if (!Array.isArray(groupsToCleanUp)) {
        return;
      }
      /**
       * Since the groups can be nested on many levels, this check filters the correct identifiers to go one level down for each iteration
       */
      const groupsToCleanUpArray = groupsToCleanUp.filter(el => el.match(/\|\d+\/(?![^|]*\|\d+\/)/)) 
      groupsToCleanUpArray.forEach((g) => removeGroup('', g))
    }

    onDelete(groupId)
  };

  return (
    <StyledGroupContainer
      isDone={isDone}
      className='group-container'
      isOpenGroup={isOpenGroup}
    >
      {openModal && <ComponentModal
        mainMessage={`${identifier}/${groupId}`}
        isOpen={openModal}
        setIsOpen={setOpenModal}
        onClickDone={onClickDone}
        isFullScreen={width <= 768}
        children={
          <StyledFormElementsContainer>
            <FormInputs 
              elements={groupElements} 
              setValue={formContext.setValue} 
              onChildChangeValue={onChildChangeValue} 
              isModal={true} 
              isInsideGroupModal={true} 
              showContent={true}
            />
            <MediaFormControls />
          </StyledFormElementsContainer>
        }
      />}
      <div
        className={containsInputs ? 'title-container clickable-title' : 'title-container'}
        onClick={openGroup}
        role='button'
        tabIndex='0'
      >
        <div className='completed-circle'>
          <ProgressCircle progress={completedPercentage} />

          <CheckIcon
            className='check-icon'
            style={{ opacity: completedPercentage === 100 ? 1 : 0 }}
          />
        </div>

        <h5>{`${identifier}/${groupId}`}</h5>

        {containsInputs && <div className='group-contains-inputs'><FormIcon /></div>}

        <button
          type='button'
          aria-label='Delete Group'
          onClick={() => removeGroup(groupId)}
          className='close-btn'
        >
          <CloseIcon />
        </button>
      </div>
      <div className='repeat-group-inputs-container'>
        <FormInputs 
          elements={groupElements} 
          setValue={formContext.setValue} 
          onChildChangeValue={onChildChangeValue} 
          isInsideGroupModal={true} 
          showContent={false} 
        />
      </div>
    </StyledGroupContainer>
  );
};

Group.propTypes = {
  identifier: PropTypes.string.isRequired,
  groupElements: PropTypes.arrayOf(
    PropTypes.shape({ identifier: PropTypes.string, title: PropTypes.string })
  ).isRequired,
  groupId: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onChangeValue: PropTypes.func.isRequired,
  formContext: customPropTypes.formContext.isRequired,
};

export default Group;
